import type { CSSProperties } from 'react';
import type { StatsWindow } from '@tv-casino/shared';
import type { CasinoLobbyVM, LobbyTab } from '@tv-casino/viewmodels/components/casinoLobbyModel.js';
import type { FloorMarketVM } from '@tv-casino/viewmodels/components/floorMarketModel.js';
import { MemberMarket } from './MemberMarket.js';
import { ProfileBadge } from './ProfileBadge.js';
import { WindowPicker } from './WindowPicker.js';
import type { MemberMarketVM } from '@tv-casino/viewmodels/components/memberMarketModel.js';
import styles from './CasinoLobby.module.css';

/**
 * The phone's casino lobby, in two screens. **Tables** is the picker — the
 * lineup and nothing else, so sitting down stays one glance. **Stats** is the
 * numbers — this member's market desk and counters, then the floor: every
 * other player's public chip desk. Pure render — labels come from the models,
 * fetching lives in the route.
 */
export function CasinoLobby({
  vm,
  market,
  floor,
  onTab,
  onSit,
  onLoadMoreTrades,
  onLoadMoreLedger,
  tradesBusy,
  ledgerBusy,
  floorWindow,
  onFloorWindow,
}: {
  vm: CasinoLobbyVM;
  /** Quote desk when this phone has a membership; omitted for newcomers. */
  market?: MemberMarketVM | null;
  /** The other players' public desks; omitted until the board has landed. */
  floor?: FloorMarketVM | null;
  /** Switch between the lineup and the numbers. */
  onTab?: (tab: LobbyTab) => void;
  /** Pick a table by its permanent room code. */
  onSit?: (tableCode: string) => void;
  onLoadMoreTrades?: () => void;
  onLoadMoreLedger?: () => void;
  tradesBusy?: boolean;
  ledgerBusy?: boolean;
  /** The window every stats card quotes; the picker hides without a setter. */
  floorWindow?: StatsWindow;
  /** Cut the desk and the floor to a new window; the route owns the refetch. */
  onFloorWindow?: (window: StatsWindow) => void;
}) {
  if (vm.loading) {
    return <p className={styles.dim}>Finding the casino…</p>;
  }

  const toneClass =
    vm.resultTone === 'win' ? styles.win : vm.resultTone === 'loss' ? styles.loss : styles.even;
  const showDesk = market?.ready === true;

  const floorTone = (tone: 'win' | 'loss' | 'even') =>
    tone === 'win' ? styles.win : tone === 'loss' ? styles.loss : styles.even;

  return (
    <section className={styles.lobby} aria-label="Casino lobby">
      <div className={styles.head}>
        <h2 className={styles.venue}>{vm.casinoName}</h2>
        {/* A private venue hands phones no code — the line disappears. */}
        {vm.joinCode && <p className={styles.code}>{vm.joinCode}</p>}
      </div>

      <nav className={styles.tabs} aria-label="Lobby navigation">
        {vm.tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={tab.active ? styles.tabOn : styles.tab}
            aria-current={tab.active ? 'page' : undefined}
            onClick={() => onTab?.(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {vm.tab === 'tables' && (
        <>
          <ul className={styles.tables}>
            {vm.tables.map((table) => (
              <li
                key={table.id}
                className={[
                  styles.tableRow,
                  table.selected ? styles.tableActive : '',
                  table.closed ? styles.tableClosed : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span>
                  {table.name} · {table.gameLabel}
                </span>
                <span className={styles.note}>{table.note}</span>
                {table.actionLabel && onSit && (
                  <button
                    type="button"
                    className={styles.switchTable}
                    onClick={() => onSit(table.code)}
                  >
                    {table.actionLabel}
                  </button>
                )}
              </li>
            ))}
          </ul>
          {vm.tables.length === 0 && (
            <p className={styles.dim}>No tables yet — the owner sets the floor up.</p>
          )}
        </>
      )}

      {vm.tab === 'stats' && (
        <>
          {/* One window cuts every card on the screen — this desk, its line
              and the floor — so the strip heads the screen, as on native. */}
          {floorWindow && onFloorWindow && (
            <WindowPicker value={floorWindow} onChange={onFloorWindow} />
          )}
          {showDesk ? (
            <MemberMarket
              vm={market}
              onLoadMoreTrades={onLoadMoreTrades}
              onLoadMoreLedger={onLoadMoreLedger}
              tradesBusy={tradesBusy}
              ledgerBusy={ledgerBusy}
              collapsible
            />
          ) : (
            <div className={styles.balanceCard}>
              <p className={styles.caption}>{vm.balanceCaption}</p>
              <p className={styles.balance}>{vm.balanceLabel}</p>
              <p className={`${styles.result} ${toneClass}`}>{vm.resultLabel}</p>
            </div>
          )}

          <div className={styles.stats}>
            {vm.emptyStatsLine ? (
              <p className={styles.dim}>{vm.emptyStatsLine}</p>
            ) : (
              vm.statGroups.map((group) => (
                <div key={group.title} className={styles.group}>
                  <p className={styles.groupTitle}>{group.title}</p>
                  <dl className={styles.statGrid}>
                    {group.lines.map((line) => (
                      <div key={line.label}>
                        <dt className={styles.statLabel}>{line.label}</dt>
                        <dd className={styles.statValue}>{line.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))
            )}
          </div>

          {floor && (
            <section className={styles.floor} aria-label="Other players">
              <p className={styles.groupTitle}>The floor</p>
              {!floor.ready ? (
                <p className={styles.dim}>Reading the room…</p>
              ) : floor.emptyLine ? (
                <p className={styles.dim}>{floor.emptyLine}</p>
              ) : (
                <ul className={styles.floorRows}>
                  {floor.rows.map((row) => (
                    <li key={row.memberId} className={styles.floorRow}>
                      <details>
                        <summary className={styles.floorHead}>
                          <span className={styles.floorName}>
                            {row.accentHex && (
                              <span
                                className={styles.floorDot}
                                style={{ '--accent': row.accentHex } as CSSProperties}
                                aria-hidden="true"
                              />
                            )}
                            {row.name}
                            {row.hasProfile && <ProfileBadge />}
                          </span>
                          <span className={floorTone(row.tone)}>{row.netLabel}</span>
                        </summary>
                        <MemberMarket vm={row.market} quoteOnly compact />
                      </details>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}

      {vm.sitBlocked && <p className={styles.dim}>{vm.sitBlocked}</p>}
    </section>
  );
}
